import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { Fade, Snackbar as MuiSnackbar } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash/fp';
import {
  Grid,
  Heading,
  Snackbar,
  Spinner,
  AlertDialog,
  Dialog,
  DialogActions,
  Button,
  DialogContent,
} from '../../common/components';
import * as contactsActions from '../../contacts/actions';
import * as outboxActions from '../../outbox/actions';
import * as actions from '../actions';
import { OutboxDialog, SchoolForm } from '../components';
import messages from '../messages';

const S = {
  Heading: styled(Heading)`
    margin-right: 16px;
  `,
  Button: styled.button`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 0.75rem;
    line-height: 2.4;
    cursor: pointer;
    outline: none;
    :hover span {
      text-decoration: underline;
    }
  `,
  Data: styled.data`
    margin-left: 2px;
    color: ${({ theme }) => theme.palette.secondary.main};
  `,
  ErrorDialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  RetryButton: styled(Button)`
    border-color: ${({ theme }) => theme.palette.error.main};
    color: ${({ theme }) => theme.palette.error.main};
  `,
};

const Writing = ({ location, match }) => {
  const [redirect, setRedirect] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(null);
  const [shown, setShown] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogText, setErrorDialogText] = useState('');
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorSnackbarText, setErrorSnackbarText] = useState('');
  const [errorPdfDialogOpen, setErrorPdfDialogOpen] = useState(false);
  const [errorPdf, setErrorPdf] = useState(false);

  const created = useSelector(state => state.outbox.created);
  const deleted = useSelector(state => state.outbox.deleted);
  const updated = useSelector(state => state.outbox.updated);
  const draft = useSelector(state => state.outbox.draft);
  const drafts = useSelector(state => state.outbox.drafts);
  const newsletter = useSelector(state => state.writing.newsletter);
  const groups = useSelector(state => state.contacts.groups);
  const targets = useSelector(state => state.writing.targets);

  const dispatch = useDispatch();

  const timerId = useRef(null);

  const openOutboxDialog = useCallback(() => setOpen('outbox'), []);
  const closeDialog = useCallback(() => setOpen(null), []);
  const fetchDraft = useCallback((...args) => {
    dispatch(outboxActions.fetchDraftRequest(...args));
  }, [dispatch]);
  const fetchDrafts = useCallback(() => dispatch(outboxActions.fetchDraftsRequest({
    sid: match.params.sid,
  })), [dispatch, match.params.sid]);
  const createDraft = useCallback((...args) => dispatch(outboxActions.createDraftRequest(...args)), [dispatch]);
  const deleteDraft = useCallback((...args) => dispatch(outboxActions.deleteDraftRequest(...args)), [dispatch]);
  const updateDraft = useCallback((...args) => dispatch(outboxActions.updateDraftRequest(...args)), [dispatch]);
  const fetchTargets = useCallback((...args) => dispatch(actions.fetchTargetsRequest(...args)), [dispatch]);
  const fetchGroups = useCallback((...args) => dispatch(contactsActions.fetchGroupsRequest(...args)), [dispatch]);
  const createNewsletter = useCallback((values) => {
    if (errorPdf) {
      setErrorPdfDialogOpen(true);
      return;
    }
    dispatch(actions.createNewsletterRequest({
      values,
      sid: match.params.sid,
    }));
  }, [dispatch, errorPdf, match.params.sid]);
  const initDraft = useCallback(() => dispatch(outboxActions.initDraft()), [dispatch]);
  // TODO: 작성 중이었던 내용을 임시 저장 후 새로운 내용을 불러왔을 때 이전에 저장한 내역을 덮어씌우지 않도록 하기 위한 처리 필요
  const handleSave = useCallback((values) => {
    if (errorPdf) {
      setErrorPdfDialogOpen(true);
      return;
    }
    if (saved) {
      updateDraft({ values, sid: match.params.sid, did: get('did', location.state) || created.data.draft || get('id', draft.data) });
    } else {
      createDraft({ values, sid: match.params.sid });
    }
  },
  [errorPdf, saved, updateDraft, match.params.sid, location.state, created.data.draft, draft.data, createDraft]);

  const requestFetchTargets = useCallback(() => {
    fetchTargets({ sid: match.params.sid, perPage: 100 });
  }, [
    fetchTargets,
    match.params.sid,
  ]);

  const handleErrorPdf = useCallback(() => {
    setErrorPdf(true);
    setErrorPdfDialogOpen(true);
  }, []);

  useEffect(() => {
    fetchGroups({ sid: match.params.sid });
  }, [fetchGroups, match.params.sid]);

  useEffect(() => {
    requestFetchTargets();
  }, [requestFetchTargets]);

  useEffect(() => {
    if (newsletter.data) {
      setRedirect(true);
    }
  }, [dispatch, newsletter.data]);

  useEffect(() => {
    if (get('did', location.state)) {
      fetchDraft({ sid: match.params.sid, did: get('did', location.state) });

      setSaved(true);
    }
  }, [fetchDraft, initDraft, location.state, match.params.sid]);

  useEffect(() => () => {
    initDraft();
  }, [initDraft]);

  useEffect(() => {
    if (created.data.draft) {
      setSaved(true);

      setShown(true);

      const timeoutId = setTimeout(() => setShown(false), 3000);

      return () => {
        dispatch(outboxActions.initCreated());

        clearTimeout(timeoutId);
      };
    }

    return () => {};
  }, [created.data.draft, dispatch]);

  useEffect(() => {
    if (updated.data.draft) {
      setShown(true);

      timerId.current = setTimeout(() => setShown(false), 3000);

      dispatch(outboxActions.initUpdated());
    }
  }, [dispatch, updated.data.draft]);

  useEffect(() => () => clearTimeout(timerId.current), []);

  useEffect(() => {
    if (newsletter.error) {
      setErrorDialogOpen(true);
      setErrorDialogText('가정통신문 전송에 실패하였습니다. 잠시 후 다시 시도해 주세요.');
    } else if (targets.error) {
      setErrorDialogOpen(true);
      setErrorDialogText('전송 대상을 볼러오지 못했습니다. 다시 시도해 주세요.');
    }
  }, [
    newsletter.error,
    targets.error,
  ]);

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
  };

  const handlePdfErrorDialogClose = () => setErrorPdfDialogOpen(false);

  const handleRetryErrorDialog = useCallback(() => {
    handleCloseErrorDialog();
    requestFetchTargets();
  }, [requestFetchTargets]);

  const handleChangePdf = useCallback(() => {
    setErrorPdf(false);
  }, []);

  useEffect(() => {
    if (drafts.error) {
      setErrorSnackbarText('임시저장글을 불러오는데 실패하였습니다.');
      setErrorSnackbarOpen(true);
    } else if (created.error.draft || updated.error.draft) {
      setErrorSnackbarText('임시저장에 실패하였습니다.');
      setErrorSnackbarOpen(true);
    } else if (deleted.error.drafts
                 && deleted.error.drafts.code !== 'storage/object-not-found') {
      setErrorSnackbarText('삭제에 실패하였습니다. 다시 시도해주세요.');
      setErrorSnackbarOpen(true);
    } else if (draft.error) {
      setErrorSnackbarText('가정통신문을 불러오는데 실패하였습니다. 다시 시도해주세요.');
      setErrorSnackbarOpen(true);
    }
  }, [
    drafts.error,
    created.error.draft,
    updated.error.draft,
    deleted.error.drafts,
    draft.error,
  ]);

  const handleCloseErrorSnackbar = () => {
    setErrorSnackbarOpen(false);
  };

  useEffect(() => () => {
    dispatch(actions.clearNewsletterError());
    dispatch(actions.clearTargetsError());
    dispatch(outboxActions.clearDraftsError());
    dispatch(outboxActions.clearCreatedErrorDraft());
    dispatch(outboxActions.clearUpdatedErrorDraft());
    dispatch(outboxActions.clearDeletedErrorDrafts());
    dispatch(outboxActions.clearDraftError());
  }, [dispatch]);

  return (
    <>
      <Grid container spacing={2} padding={{ top: 24, bottom: 72 }}>
        <Grid container item xs={12} margin={{ bottom: 24 }} alignItems="flex-end">
          <S.Heading>
            <FormattedMessage {...messages.writingHeading} />
          </S.Heading>
          <S.Button type="button" onClick={openOutboxDialog}>
            <span>
              <FormattedMessage {...messages.writingOutboxButton} />
            </span>
            {/* <S.Data value="100">100</S.Data> */}
          </S.Button>
        </Grid>
        <Grid item xs={12}>
          <SchoolForm
            draft={draft}
            groups={groups}
            redirect={redirect}
            targets={targets}
            onSave={handleSave}
            onSubmit={createNewsletter}
            onChangePdf={handleChangePdf}
            onErrorPdf={handleErrorPdf}
          />
        </Grid>
      </Grid>
      <OutboxDialog
        drafts={drafts}
        open={open === 'outbox'}
        setSaved={setSaved}
        onClick={fetchDraft}
        onClose={closeDialog}
        onDelete={deleteDraft}
        onEntered={fetchDrafts}
      />
      {(created.loading.draft
        || updated.loading.draft
        || deleted.loading.draft
        || draft.loading
        || drafts.loading
        || newsletter.loading) && <Spinner />}
      {redirect && (
        <Redirect
          to={{
            pathname: `/schools/${match.params.sid}/school-newsletters`,
            state: { from: location.pathname },
          }}
        />
      )}
      <Snackbar
        open={shown}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'save-draft-message',
        }}
        message={(
          <span id="save-draft-message">
            임시저장되었습니다.
          </span>
        )}
      />

      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
      >
        <S.ErrorDialogContent id="alert-dialog-title">{errorDialogText}</S.ErrorDialogContent>
        <DialogActions>
          {newsletter.error ? (
            <Button
              variant="outlined"
              onClick={handleCloseErrorDialog}
            >
              닫기
            </Button>
          ) : (
            <S.RetryButton
              onClick={handleRetryErrorDialog}
              variant="outlined"
            >
              재시도
            </S.RetryButton>
          )}
        </DialogActions>
      </Dialog>

      <MuiSnackbar
        open={errorSnackbarOpen}
        message={errorSnackbarText}
        onClose={handleCloseErrorSnackbar}
        autoHideDuration={5000}
      />
      <AlertDialog open={errorPdfDialogOpen} onClose={handlePdfErrorDialogClose}>
        PDF를 불러오는데 실패했습니다. PDF 파일을 다시 업로드 해주세요.
      </AlertDialog>
    </>
  );
};

Writing.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    state: PropTypes.shape({
      did: PropTypes.string,
    }),
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default Writing;
